import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import {
  Check,
  RotateCcw,
  Trash2,
  Copy,
  FileText,
  Plus,
  Target,
  Users,
  Sparkles,
  Star,
} from "lucide-react";
import { getMatiereInfo, getSessionTypeInfo, JournalStatusBadge } from "./constants";

export function SessionCard({
  session,
  onStatusChange,
  onDelete,
  onDuplicate,
  onEdit,
  onGenerateVariants,
  onCreateResource,
  onOpenResource,
  isDragging = false,
}) {
  const matiereInfo = getMatiereInfo(session.matiere);
  const sessionTypeInfo = getSessionTypeInfo(session.session_type);
  const Icon = matiereInfo?.icon;
  const hasSkills = session.skills && session.skills.length > 0;
  const hasResource = !!session.resource_id;
  const hasDifferentiation = session.has_differentiation;
  const mainSkill = session.main_skill;

  return (
    <TooltipProvider>
      <div
        data-testid={`session-card-${session.id}`}
        className={`rounded-lg border-2 transition-all hover:shadow-md relative max-h-[130px] overflow-hidden ${
          isDragging ? "shadow-lg ring-2 ring-indigo-400" : ""
        }`}
        style={{
          backgroundColor: `${matiereInfo?.color}15`,
          borderColor: matiereInfo?.color,
          borderLeftWidth: "4px",
        }}
      >
        {/* Color band at top */}
        <div
          className="h-1 w-full"
          style={{ backgroundColor: matiereInfo?.color }}
        />

        <div className="p-2">
          <div className="flex items-start justify-between gap-1 mb-1">
            <div className="flex items-center gap-1.5 min-w-0 flex-1">
              {Icon && (
                <Icon
                  className="w-4 h-4 flex-shrink-0"
                  style={{ color: matiereInfo?.color }}
                />
              )}
              <span
                className="font-semibold text-xs truncate"
                style={{ color: matiereInfo?.color }}
              >
                {session.title}
              </span>
            </div>
            <JournalStatusBadge
              status={session.status}
              className="text-[10px] px-1.5 py-0"
            />
          </div>

          {/* Session type badge */}
          <div className="flex flex-wrap gap-1 mb-1">
            <Badge
              variant="outline"
              className={`text-[9px] px-1 py-0 ${sessionTypeInfo?.color}`}
            >
              {sessionTypeInfo?.label}
            </Badge>
            {session.niveau && (
              <Badge variant="outline" className="text-[9px] px-1 py-0">
                {session.niveau.toUpperCase()}
              </Badge>
            )}
          </div>

          {/* Main skill */}
          {mainSkill && (
            <div className="flex items-center gap-1 text-[10px] text-slate-600 mb-1">
              <Star className="w-2.5 h-2.5 text-yellow-500" />
              <span className="truncate">{mainSkill.label}</span>
            </div>
          )}

          {/* Resource link */}
          {session.resource_title && (
            <div
              className="flex items-center gap-1 text-[10px] text-indigo-600 mb-1 hover:underline cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                onOpenResource(session.resource_id);
              }}
            >
              <FileText className="w-2.5 h-2.5" />
              <span className="truncate">{session.resource_title}</span>
            </div>
          )}

          {/* Indicators */}
          <div className="flex items-center gap-1 mt-1">
            {hasSkills && (
              <Tooltip>
                <TooltipTrigger>
                  <Target className="w-3 h-3 text-indigo-500" />
                </TooltipTrigger>
                <TooltipContent>
                  {session.skills.length} compétence(s) liée(s)
                </TooltipContent>
              </Tooltip>
            )}
            {hasDifferentiation && (
              <Tooltip>
                <TooltipTrigger>
                  <Users className="w-3 h-3 text-green-500" />
                </TooltipTrigger>
                <TooltipContent>Différenciation active</TooltipContent>
              </Tooltip>
            )}
          </div>

          {/* Actions */}
          <div
            className="flex items-center gap-1 mt-1 border-t pt-1"
            style={{ borderColor: `${matiereInfo?.color}30` }}
            onClick={(e) => e.stopPropagation()}
          >
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-5 w-5 p-0"
                  onClick={() => onStatusChange(session.id, "fait")}
                >
                  <Check className="w-3 h-3 text-green-600" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Marquer fait</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-5 w-5 p-0"
                  onClick={() => onStatusChange(session.id, "reporte")}
                >
                  <RotateCcw className="w-3 h-3 text-orange-600" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Reporter</TooltipContent>
            </Tooltip>
            {/* Create/Open Resource */}
            {hasResource ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-5 w-5 p-0"
                    onClick={() => onOpenResource(session.resource_id)}
                  >
                    <FileText className="w-3 h-3 text-indigo-600" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Ouvrir la fiche</TooltipContent>
              </Tooltip>
            ) : (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-5 w-5 p-0"
                    onClick={() => onCreateResource(session.id)}
                  >
                    <Plus className="w-3 h-3 text-green-600" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Créer une fiche</TooltipContent>
              </Tooltip>
            )}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-5 w-5 p-0"
                  onClick={() => onDuplicate(session.id)}
                >
                  <Copy className="w-3 h-3 text-blue-600" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Dupliquer</TooltipContent>
            </Tooltip>
            {hasResource &&
              session.target_student_ids?.length > 0 &&
              !hasDifferentiation && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-5 w-5 p-0"
                      onClick={() => onGenerateVariants(session.id)}
                    >
                      <Sparkles className="w-3 h-3 text-purple-600" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Générer variantes</TooltipContent>
                </Tooltip>
              )}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-5 w-5 p-0 ml-auto"
                  onClick={() => onDelete(session.id)}
                >
                  <Trash2 className="w-3 h-3 text-red-500" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Supprimer</TooltipContent>
            </Tooltip>
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
}

export default SessionCard;
